"use client";

import Image from "next/image";
import { useEffect, useMemo, useState } from "react";
import { cn } from "@/lib/utils";
import { X } from "lucide-react";
import type { AvailabilitySummary } from "@/lib/v2/availability";
import { floorPlanImageFor } from "@/lib/v2/availability";
import { formatArea } from "@/lib/v2/format";
import { generateImageURL } from "@/lib/utils";
import { Button } from "../Button";
import { FloorPlanCard } from "../FloorPlanCard";

type Plan = AvailabilitySummary["byPlan"][number] & { image?: string };

function parsedPlanCode(file: string) {
  const base = file.replace(/\.[^.]+$/, "");
  return base.split("_")[0] || base;
}

export function ProjectFloorPlans({
  projectName,
  floorImages,
  plans,
  onAsk,
}: {
  projectName: string;
  floorImages: string[];
  plans: AvailabilitySummary["byPlan"];
  onAsk: (interest: { plan: string; type?: string }) => void;
}) {
  const [selected, setSelected] = useState<Plan | null>(null);
  const [activeType, setActiveType] = useState<string | null>(null);
  const resolvedPlans = useMemo<Plan[]>(() => {
    if (plans.length > 0) {
      return plans.map((plan) => {
        const file = floorPlanImageFor(plan.plan, floorImages);
        return {
          ...plan,
          image: file
            ? generateImageURL(projectName, "floor_images", file)
            : undefined,
        };
      });
    }
    return floorImages.map((file) => ({
      plan: parsedPlanCode(file),
      type: "",
      total: 0,
      available: 0,
      minArea: 0,
      maxArea: 0,
      minPrice: null,
      maxPrice: null,
      image: generateImageURL(projectName, "floor_images", file),
    }));
  }, [floorImages, plans, projectName]);

  useEffect(() => {
    if (!selected) return;
    const close = (event: KeyboardEvent) => {
      if (event.key === "Escape") setSelected(null);
    };
    window.addEventListener("keydown", close);
    return () => window.removeEventListener("keydown", close);
  }, [selected]);

  if (resolvedPlans.length === 0) return null;

  const groups = Array.from(
    resolvedPlans.reduce((map, plan) => {
      const key = plan.type || "Floor plans";
      map.set(key, [...(map.get(key) ?? []), plan]);
      return map;
    }, new Map<string, Plan[]>()),
  );

  const activeGroup = groups.find(([type]) => type === activeType) ?? groups[0];
  const [activeName, activePlans] = activeGroup;
  const activeLeft = activePlans.reduce((total, plan) => total + plan.available, 0);

  return (
    <>
      <div>
        {groups.length > 1 && (
          <div className="-mx-4 mb-6 flex gap-2 overflow-x-auto px-4 pb-1 [scrollbar-width:none] md:mx-0 md:flex-wrap md:px-0 [&::-webkit-scrollbar]:hidden" role="tablist" aria-label="Bedroom types">
            {groups.map(([type, group]) => {
              const left = group.reduce((total, plan) => total + plan.available, 0);
              const active = type === activeName;
              return (
                <button
                  key={type}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => setActiveType(type)}
                  className={cn(
                    "flex shrink-0 items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium transition-colors",
                    active ? "border-ink bg-ink text-white" : "border-line bg-white text-ink hover:bg-surface"
                  )}
                >
                  {type}
                  {plans.length > 0 && (
                    <span className={cn("rounded-full px-2 py-0.5 text-[11px] font-semibold", active ? "bg-white/15 text-white" : left > 0 ? "bg-avail-soft text-avail" : "bg-sold-soft text-sold")}>
                      {left > 0 ? `${left} left` : "Sold out"}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}
        <div className="mb-4 flex flex-wrap items-baseline justify-between gap-2">
          <h3 className="font-display text-xl font-semibold text-ink">{activeName}</h3>
          {plans.length > 0 && (
            <p className="text-sm text-ink-muted">
              {activePlans.length} {activePlans.length === 1 ? "plan" : "plans"}, {activeLeft} {activeLeft === 1 ? "unit" : "units"} left
            </p>
          )}
        </div>
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-5" role="tabpanel">
          {activePlans.map((plan) => (
            <FloorPlanCard
              key={`${plan.type}-${plan.plan}`}
              plan={plan.plan}
              type={plan.type}
              area={plan.minArea > 0 ? { min: plan.minArea, max: plan.maxArea } : undefined}
              unitsLeft={plans.length > 0 ? plan.available : undefined}
              image={plan.image}
              onOpen={() => setSelected(plan)}
            />
          ))}
        </div>
      </div>

      {selected && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-ink/60 p-0 sm:items-center sm:p-4"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setSelected(null);
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="floor-plan-title"
            className="relative max-h-[92vh] w-full overflow-y-auto rounded-t-2xl bg-white p-5 sm:max-w-4xl sm:rounded-2xl md:p-7"
          >
            <button
              type="button"
              onClick={() => setSelected(null)}
              aria-label="Close floor plan"
              className="absolute right-4 top-4 z-10 rounded-full border border-line bg-white p-2 text-ink"
            >
              <X aria-hidden="true" className="size-5" />
            </button>
            <div className="grid gap-6 md:grid-cols-[1.4fr_1fr] md:items-center">
              <div className="relative aspect-square overflow-hidden rounded-xl bg-surface">
                {selected.image ? (
                  <Image
                    src={selected.image}
                    alt={`${selected.plan} floor plan`}
                    fill
                    unoptimized
                    className="object-contain p-4"
                    sizes="(min-width: 768px) 55vw, 100vw"
                  />
                ) : (
                  <div className="flex size-full items-center justify-center font-display text-3xl font-semibold text-ink-faint">
                    {selected.plan}
                  </div>
                )}
              </div>
              <div>
                <h2 id="floor-plan-title" className="pr-10 font-display text-2xl font-semibold text-ink">
                  {selected.plan}
                </h2>
                {selected.type && <p className="mt-2 text-ink-muted">{selected.type}</p>}
                {selected.minArea > 0 && (
                  <p className="mt-5 text-sm text-ink-muted">
                    Size: {selected.minArea === selected.maxArea ? formatArea(selected.minArea) : `${formatArea(selected.minArea)} to ${formatArea(selected.maxArea)}`}
                  </p>
                )}
                {plans.length > 0 && (
                  <p className="mt-2 text-sm font-semibold text-avail">
                    {selected.available} {selected.available === 1 ? "unit" : "units"} left
                  </p>
                )}
                <Button
                  className="mt-6"
                  onClick={() => {
                    setSelected(null);
                    onAsk({ plan: selected.plan, type: selected.type || undefined });
                  }}
                >
                  Ask about this layout
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
